/**
 * Keeps a recording across a full page reload.
 *
 * The step list goes to `sessionStorage` (this tab only); the screenshots, which are too
 * big for it, go to IndexedDB. These are **live-data** screenshots (G20), so they must
 * not outlive their use on the device:
 *
 * - removed on upload and on discard (`clearRecording`);
 * - a recording restores only for the user who started it — a different or signed-out
 *   user gets it deleted instead (`wb.userId`, written by the auth repository);
 * - anything older than `MAX_AGE_MS` is swept whenever an author's page loads (the
 *   recorder restores lazily, for authors only), which also catches a tab closed
 *   mid-recording, whose `sessionStorage` is gone but whose images are not.
 *
 * Every call swallows storage failures (private windows, blocked site data): losing the
 * recording on reload is the documented fallback, never a broken page.
 */

import type { RecordedLocator } from '../types';

const SESSION_KEY = 'wb.gms.recording';
const DB_NAME = 'wb-gms-recorder';
const STORE = 'screenshots';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface PersistedStep {
  key: string;
  text: string;
  locator: RecordedLocator;
}

export interface PersistedRecording {
  id: string;
  owner: string;
  status: 'recording' | 'finishing';
  startRoute: string;
  startScreenName: string;
  steps: PersistedStep[];
  savedAt: number;
}

interface StoredShot {
  id: string;
  recordingId: string;
  savedAt: number;
  file: Blob;
}

/** Who is signed in, as the auth repository records it. Empty when nobody is. */
export function currentOwner(): string {
  try {
    return localStorage.getItem('wb.userId') ?? '';
  } catch {
    return '';
  }
}

export function newRecordingId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function saveRecording(recording: PersistedRecording): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(recording));
  } catch {
    // Full or blocked: the recording just will not survive a reload.
  }
}

/**
 * The recording to restore in this tab, or null. Returns null — and deletes it — when it
 * belongs to someone else, nobody is signed in, or it is too old.
 */
export function loadRecording(): PersistedRecording | null {
  let recording: PersistedRecording | null = null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    recording = raw ? (JSON.parse(raw) as PersistedRecording) : null;
  } catch {
    recording = null;
  }
  if (!recording) return null;
  const owner = currentOwner();
  const fresh = Date.now() - recording.savedAt < MAX_AGE_MS;
  if (!owner || recording.owner !== owner || !fresh || !Array.isArray(recording.steps)) {
    clearRecording(recording.id);
    return null;
  }
  return recording;
}

/**
 * Open the database, creating the store if it is missing. A database can exist without
 * it (opened elsewhere without an upgrade handler), and a missing store would otherwise
 * fail every save silently for good — so that case reopens one version up to repair it.
 */
function openDb(version?: number): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, version);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE)) {
          request.result.createObjectStore(STORE, { keyPath: 'id' });
        }
      };
      request.onsuccess = () => {
        const db = request.result;
        if (db.objectStoreNames.contains(STORE)) return resolve(db);
        const next = db.version + 1;
        db.close();
        if (version !== undefined) return resolve(null); // already tried to repair
        void openDb(next).then(resolve);
      };
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/** Run one transaction and close the connection. Resolves to null on any failure. */
async function withStore<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T> | void
): Promise<T | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise<T | null>((resolve) => {
    try {
      const transaction = db.transaction(STORE, mode);
      const request = work(transaction.objectStore(STORE));
      transaction.oncomplete = () => {
        db.close();
        resolve(request ? (request.result ?? null) : null);
      };
      transaction.onerror = () => {
        db.close();
        resolve(null);
      };
      transaction.onabort = transaction.onerror;
    } catch {
      db.close();
      resolve(null);
    }
  });
}

export function saveScreenshot(recordingId: string, stepKey: string, file: File): void {
  const shot: StoredShot = {
    id: `${recordingId}/${stepKey}`,
    recordingId,
    savedAt: Date.now(),
    file,
  };
  void withStore('readwrite', (store) => store.put(shot));
}

export async function loadScreenshot(recordingId: string, stepKey: string): Promise<File | null> {
  const shot = await withStore<StoredShot | undefined>('readonly', (store) =>
    store.get(`${recordingId}/${stepKey}`)
  );
  if (!shot?.file) return null;
  return new File([shot.file], `${stepKey}.png`, { type: 'image/png' });
}

/** Delete matching screenshots: one recording's, or (with no id) every stale one. */
function deleteShots(match: (shot: StoredShot) => boolean): void {
  void withStore('readwrite', (store) => {
    const cursor = store.openCursor();
    cursor.onsuccess = () => {
      const current = cursor.result;
      if (!current) return;
      if (match(current.value as StoredShot)) current.delete();
      current.continue();
    };
  });
}

/** Forget a recording entirely: its step list and its screenshots. */
export function clearRecording(recordingId: string): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Nothing to clear.
  }
  if (recordingId) deleteShots((shot) => shot.recordingId === recordingId);
}

/** Delete screenshots older than a day, left behind by a tab closed mid-recording. */
export function sweepStaleScreenshots(): void {
  const cutoff = Date.now() - MAX_AGE_MS;
  deleteShots((shot) => shot.savedAt < cutoff);
}
